import React, { FC } from 'react'
import { graphql, usePaginationFragment } from 'react-relay'

import {
  Column,
  EventCard,
  Grid,
  HorizontalLine,
  Stack,
  Text,
  useBreakpoint,
} from '@components'

import {
  PresentationEvents_presentation$key,
  PresentationEvents_presentation$data,
} from './__generated__/PresentationEvents_presentation.graphql'
import { useCardInitialCounts } from './useCardInitialCounts'

type PresentationEventsProps = {
  presentation: PresentationEvents_presentation$key
}

const PresentationEvents: FC<PresentationEventsProps> = (props) => {
  const { data } = usePaginationFragment(
    graphql`
      fragment PresentationEvents_presentation on Presentation
      @refetchable(queryName: "PresentationEventsPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 4 }
        cursor: { type: "Cursor" }
      ) {
        events(first: $count, after: $cursor)
          @connection(key: "PresentationEvents_presentation_events") {
          edges {
            node {
              id
              ...EventCard_event
            }

            cursor
          }
        }
      }
    `,
    props.presentation
  )

  if (data.events.edges.length === 0) {
    return null
  }

  return (
    <Stack gap={5}>
      <Stack direction='horizontal' gap={4} alignItems='center'>
        <Text size={4} variant='cardHeading'>
          Presented at these Events
        </Text>
        <HorizontalLine variant='cardHeading' />
      </Stack>
      <Stack>
        <EventCardGrid edges={data.events.edges} />
      </Stack>
    </Stack>
  )
}

const EventCardGrid: FC<{
  edges: PresentationEvents_presentation$data['events']['edges']
}> = ({ edges }) => {
  const breakpoint = useBreakpoint()
  const initialCounts = useCardInitialCounts()

  if (breakpoint === 0) {
    return (
      <Stack gap={4}>
        {edges.map((edge) => (
          <EventCard event={edge.node} key={edge.node.id} />
        ))}
      </Stack>
    )
  }

  const eventGroups = edges.reduce<Array<Array<(typeof edges)[number]>>>(
    (memo, event, index) => {
      if (index % initialCounts[breakpoint] === 0) {
        memo.push([event])
      } else {
        memo[memo.length - 1].push(event)
      }

      return memo
    },
    []
  )

  return (
    <Stack gap={4}>
      {eventGroups.map((group, index) => {
        return (
          <Stack key={index}>
            <Grid gap={4}>
              {group.map((edge) => {
                return (
                  <Column key={edge.cursor}>
                    <EventCard
                      expand
                      event={edge.node}
                      showOrganizer
                      showPresentationsOrAttendees
                    />
                  </Column>
                )
              })}

              {Array.from({
                length: initialCounts[breakpoint] - group.length,
              }).map((_, index) => (
                <Column key={index} />
              ))}
            </Grid>
          </Stack>
        )
      })}
    </Stack>
  )
}

export default PresentationEvents
